/**
 * Sheet — bottom-sheet primitive with snap-point detents.
 *
 * Mobile (<768px): slides up from the bottom with a 20px top radius, drag
 *   handle (drag-to-dismiss enabled), scrim backdrop, and snap-points per
 *   detent. Composed on top of Vaul's `Drawer.*` primitives.
 * Desktop (≥768px): centred modal with 12px radius (no drag handle). Composed
 *   on top of Radix `Dialog.*` primitives.
 *
 * Both branches preserve the existing `proof-sheet-*` class names and CSS so
 * the visible chrome is byte-identical to the hand-rolled implementation. The
 * behavioural commodities (focus trap, focus restore, scroll-lock, ESC,
 * outside-click, `inert` on background, `aria-modal="true"`) are now solved at
 * the primitive layer instead of being re-implemented in five consumers.
 *
 * ── Two API shapes — both supported ──────────────────────────────────────────
 *
 * 1. **Legacy "always-mounted-when-open" shape** (existing consumers:
 *    UserMenu mobile Sheet, VariationSwitcherSheet, SectionRatingSheet, log-a-cook
 *    SectionRatingSheet):
 *
 *      <Sheet detent="medium" onDismiss={() => setOpen(false)} aria-label="Account">
 *        <SheetHeader title="Account" onDismiss={onDismiss} />
 *        <div className="proof-sheet-body">…</div>
 *      </Sheet>
 *
 *    The parent renders the Sheet conditionally on its own open state. The
 *    Sheet itself is always `open={true}` from Vaul/Radix's perspective.
 *    `onDismiss` is the single uniform close callback (ESC, scrim tap, drag,
 *    × button).
 *
 * 2. **New "controlled snap-point" shape** (Wave 5 sheets — new-version,
 *    spin-off, discard, closed-edit, drafts-drawer — and any future consumer
 *    that needs unsaved-changes guard or snap-point semantics):
 *
 *      <Sheet
 *        open={open}
 *        onOpenChange={setOpen}
 *        snapPoints={["peek", "full"]}
 *        defaultSnapPoint="peek"
 *        dismissGuard={() => unsavedChanges ? confirmDiscard() : true}
 *        aria-label="New version"
 *      >
 *        <SheetHeader title="New version" onDismiss={() => setOpen(false)} />
 *        <SheetBody>…scrollable content…</SheetBody>
 *        <SheetFooter>
 *          <button>Cancel</button>
 *          <button>Freeze draft</button>
 *        </SheetFooter>
 *      </Sheet>
 *
 *    The Sheet renders nothing when `open === false`; mounts on open. The
 *    `dismissGuard` is called before every close path; returning `false` (or
 *    a rejected/false-resolving promise) cancels the dismiss.
 *
 * Both API shapes compile to the same underlying Vaul (mobile) + Radix Dialog
 * (desktop) composition.
 *
 * ── Snap-point semantics (new API only) ──────────────────────────────────────
 *
 * `snapPoints` accepts either:
 *   - Named tokens: `["peek", "medium", "full"]` → 0.4, 0.6, 0.92 of viewport.
 *   - Numeric fractions: `[0.4, 0.92]` → custom snap-points (0 = closed, 1 = full).
 * `defaultSnapPoint` sets the initial snap. On mobile, Vaul handles the drag
 * physics between snap-points natively. On desktop, the sheet renders as a
 * centred dialog at a fixed height (the largest of the snap-points) — desktop
 * does not drag between snap-points; the dialog is a static modal.
 *
 * ── Dismiss-guard contract (new API only) ────────────────────────────────────
 *
 * `dismissGuard` is called before every close path (ESC, scrim tap, drag,
 * programmatic `onOpenChange(false)`). It must return either:
 *   - `true` or a promise resolving to `true` → close proceeds.
 *   - `false` or a promise resolving to `false` → close is cancelled; the
 *     Sheet stays open and the snap-point is unchanged.
 *   - A rejected promise → close is cancelled and the rejection is logged
 *     (Rule 8 — never silently swallow). Caller can also catch via `onError`.
 *
 * Animation hooks: Radix and Vaul both emit `data-state="open" | "closed"` on
 * Overlay + Content. The CSS scopes its `proof-sheet-*` keyframes to those
 * data-state selectors so enter/exit transitions stay declarative.
 *
 * Public API (`SheetProps`, `SheetHeaderProps`, `SheetDetent`) is unchanged
 * versus the prior hand-rolled version. New API (`SheetControlledProps`,
 * `SheetSnapPoint`, `SheetBodyProps`, `SheetFooterProps`) is additive — no
 * existing consumer changes.
 *
 * Design refs:
 *   gap doc A.1.3 — Sheet primitive spec.
 *   design.md §2 — hairlines not shadows; 200ms ease; no springs; no glassmorphism.
 *   design.md §4 — warm neutral tokens; no pure #FFF / #000.
 *   design.md §4.4 — radius-card 12px (desktop), radius-sheet-top 20px (mobile).
 *   branding.md §5.2.1 — no banned words in any string.
 *   CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 *   CLAUDE.md Rule 8 — dismiss-guard logs rejection paths; no silent swallow.
 *   .plan/features/2026-05-09-radix-primitive-migration.md §8 — Stage 1 scope.
 *   .plan/features/2026-05-11-framework-adoption.md §4 Spike 0.I — new-API surface.
 */

import { useSyncExternalStore, useRef, useEffect, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import "./Sheet.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export type SheetDetent = "peek" | "medium" | "large";

/**
 * Named snap-point tokens (preferred). Numbers are also accepted for callers
 * that need custom fractions. Values are fractions of the visual viewport
 * height: 0 = closed, 1 = top of viewport.
 */
export type SheetSnapPoint = "peek" | "medium" | "full" | number;

/**
 * Common props shared by both API shapes.
 */
interface SheetCommonProps {
  /** Sheet content — typically includes a SheetHeader + SheetBody (+ SheetFooter). */
  children: ReactNode;
  /** Accessible label for the dialog element. */
  "aria-label"?: string;
  /** data-testid forwarded to the panel element. */
  "data-testid"?: string;
  /**
   * Arbitrary `data-*` attributes are forwarded to the Content element. The
   * primary consumer is `data-capsule-passthrough="true"`, which the capsule
   * MutationObserver checks to decide whether to auto-minimise on mount.
   */
  [dataAttr: `data-${string}`]: string | undefined;
}

/**
 * Legacy "always-mounted-when-open" API shape.
 *
 * Used by UserMenu (mobile Sheet), VariationSwitcherSheet, SectionRatingSheet. The
 * parent renders the Sheet conditionally; the Sheet itself is always
 * `open={true}` to Vaul/Radix; `onDismiss` is the single close callback.
 */
export interface SheetProps extends SheetCommonProps {
  /** Height tier: peek (40dvh) | medium (60dvh) | large (92dvh). Desktop: ignored. */
  detent?: SheetDetent;
  /** Called when the user taps the scrim, drags the sheet down, or presses Escape. */
  onDismiss: () => void;
}

/**
 * New "controlled snap-point" API shape.
 *
 * Used by Wave 5 sheets. The parent owns `open`; the Sheet renders nothing
 * when `open === false`. `dismissGuard` runs before every close path — if it
 * returns `false` (or a falsy promise), the close is cancelled.
 */
export interface SheetControlledProps extends SheetCommonProps {
  /** Whether the sheet is open. Sheet renders nothing when false. */
  open: boolean;
  /**
   * Called when the user taps the scrim, drags down past the lowest snap-point,
   * presses Escape, or programmatic close. Always receives `false` from this
   * primitive (we don't fire `true` — that's the parent's call to open).
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Ordered list of snap-points. Named tokens resolve to fractions of the
   * visual viewport (peek=0.4, medium=0.6, full=0.92). Numbers must be
   * between 0 (exclusive) and 1 (inclusive).
   *
   * Defaults to `["full"]` (single 92% snap-point — equivalent to a modal).
   */
  snapPoints?: SheetSnapPoint[];
  /**
   * Initial snap-point. Must be a member of `snapPoints`. Defaults to the
   * first entry in `snapPoints`.
   */
  defaultSnapPoint?: SheetSnapPoint;
  /**
   * Called before every close path (ESC, scrim tap, drag, programmatic close).
   *
   * Returns `true` → close proceeds (default).
   * Returns `false` → close is cancelled; sheet stays open.
   * Returns a promise → close is cancelled until the promise settles; if the
   *   promise resolves `true`, close proceeds; if it resolves `false` or
   *   rejects, close is cancelled (rejection is logged — Rule 8).
   */
  dismissGuard?: () => boolean | Promise<boolean>;
  /**
   * Optional error callback. Called when `dismissGuard` rejects (Rule 8 —
   * caller decides whether to surface a toast or re-throw). If omitted, the
   * rejection is logged via `console.error` only.
   */
  onDismissGuardError?: (err: unknown) => void;
  /**
   * Side from which the sheet enters. Only "bottom" is supported at MVP;
   * the prop exists so callers can opt-in to side="right" etc. once we add it.
   */
  side?: "bottom";
}

/**
 * Type guard — selects the new controlled-API branch.
 *
 * The new API is identified by the presence of `onOpenChange`. Legacy callers
 * use `onDismiss`. We never accept both on the same call.
 */
function isControlled(p: SheetProps | SheetControlledProps): p is SheetControlledProps {
  return "onOpenChange" in p && typeof (p as SheetControlledProps).onOpenChange === "function";
}

// ── SheetHeader ───────────────────────────────────────────────────────────────

export interface SheetHeaderProps {
  /** Title rendered in the header. */
  title: string;
  /** Helper line rendered below the title (optional). */
  helper?: string;
  /** Called when the × button is tapped. */
  onDismiss: () => void;
}

/**
 * SheetHeader — standard header row with title, optional helper text,
 * and a dismiss button (×). Inline styles preserved verbatim from the
 * pre-Radix implementation so the visible chrome is byte-identical.
 */
export function SheetHeader({ title, helper, onDismiss }: SheetHeaderProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "var(--space-4, 16px) var(--space-5, 20px) var(--space-3, 12px)",
        borderBottom: "1px solid var(--line)",
        flexShrink: 0,
        gap: "var(--space-1, 4px)",
        position: "relative",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "17px",
          fontWeight: 600,
          color: "var(--text)",
          lineHeight: 1.3,
        }}
      >
        {title}
      </span>
      {helper && (
        <span
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "13px",
            color: "var(--muted)",
            lineHeight: 1.4,
            paddingRight: "36px",
          }}
        >
          {helper}
        </span>
      )}
      <button
        type="button"
        aria-label="Close"
        onClick={onDismiss}
        style={{
          position: "absolute",
          top: "var(--space-3, 12px)",
          right: "var(--space-4, 16px)",
          width: "28px",
          height: "28px",
          border: "none",
          // A 6% wash of the ink, NOT `--faint` (a solid mid-tone token): the
          // former `var(--faint, rgba(0,0,0,0.06))` never fell through to its
          // fallback, so the disc rendered solid and the × sat on it at ~1.06:1.
          // color-mix keeps the disc a quiet wash and holds the glyph at 4.7:1.
          background: "color-mix(in srgb, var(--text) 6%, transparent)",
          borderRadius: "50%",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--muted)",
          fontFamily: "var(--font-sans)",
          fontSize: "16px",
          lineHeight: 1,
          WebkitTapHighlightColor: "transparent",
          flexShrink: 0,
          transition: "opacity 200ms ease",
        }}
        data-testid="sheet-dismiss-btn"
      >
        ×
      </button>
    </div>
  );
}

// ── SheetBody ─────────────────────────────────────────────────────────────────

export interface SheetBodyProps {
  children: ReactNode;
  /** Optional className appended after `proof-sheet-body`. */
  className?: string;
  /** data-testid forwarded to the body element. */
  "data-testid"?: string;
}

/**
 * SheetBody — scrollable content region between the SheetHeader (sticky top)
 * and SheetFooter (sticky bottom). Uses `flex: 1 1 0` + `overflow-y: auto` so
 * arbitrary-length content scrolls inside the sheet while header + footer
 * stay pinned.
 *
 * Default padding is `var(--space-4) var(--space-5)` (16px vertical, 20px
 * horizontal — matches the SheetHeader inline padding). Pass `className` to
 * override or augment.
 */
export function SheetBody({ children, className, "data-testid": dataTestid }: SheetBodyProps) {
  const cls = className
    ? `proof-sheet-body proof-sheet-body--padded ${className}`
    : "proof-sheet-body proof-sheet-body--padded";
  return (
    /*
     * data-vaul-no-drag: prevents Vaul's shouldDrag() from treating gestures
     * that originate inside this scroll region as drag-to-dismiss events.
     *
     * Vaul (1.1.2) walks from the touch target up the DOM. When it reaches
     * a scrollable element (scrollHeight > clientHeight) with scrollTop === 0,
     * it continues up toward the root instead of halting — the role="dialog"
     * check only fires when the dialog element itself is both scrollable AND
     * at scrollTop 0. In a fixed-height sheet with flex layout the panel is
     * never over-tall (flex: 1 1 0 fills exactly), so that bail-out never
     * triggers and every downward gesture from the list top is treated as a
     * drag-to-dismiss. data-vaul-no-drag short-circuits the walk at line 981
     * of vaul/dist/index.mjs and returns false immediately, deferring to the
     * native scroll. The sheet is still dismissible via the handle, × button,
     * and scrim tap.
     */
    <div className={cls} data-testid={dataTestid} data-vaul-no-drag>
      {children}
    </div>
  );
}

// ── SheetFooter ───────────────────────────────────────────────────────────────

export interface SheetFooterProps {
  children: ReactNode;
  /** Optional className appended after `proof-sheet-footer`. */
  className?: string;
  /** data-testid forwarded to the footer element. */
  "data-testid"?: string;
}

/**
 * SheetFooter — sticky CTA row at the bottom of the sheet. Reserves
 * safe-area-inset-bottom space so the home-indicator clearance is honoured
 * (the panel's own padding-bottom handles this, but the footer adds a 1px
 * hairline border on top per design.md §2 — hairlines not shadows for
 * sticky-on-scroll treatment).
 */
export function SheetFooter({ children, className, "data-testid": dataTestid }: SheetFooterProps) {
  const cls = className ? `proof-sheet-footer ${className}` : "proof-sheet-footer";
  return (
    <div className={cls} data-testid={dataTestid}>
      {children}
    </div>
  );
}

// ── Viewport detection ────────────────────────────────────────────────────────

/**
 * Subscribe to the `(min-width: 768px)` media query.
 *
 * Server / no-`window` environments resolve to `false` (mobile) so the SSR
 * fallback matches the smaller-viewport-first design defaults. The match
 * updates live on resize (e.g. orientation change, devtools breakpoint
 * preview). useSyncExternalStore is the React-idiomatic way to consume an
 * MQL outside of a render-blocking effect.
 */
function subscribeMQL(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const mql = window.matchMedia("(min-width: 768px)");
  // Safari < 14 uses `addListener`; modern browsers use `addEventListener`.
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", callback);
    return () => mql.removeEventListener("change", callback);
  }
  mql.addListener(callback);
  return () => mql.removeListener(callback);
}

function getMQLSnapshot(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(min-width: 768px)").matches;
}

function getMQLServerSnapshot(): boolean {
  return false;
}

export function useIsDesktop(): boolean {
  return useSyncExternalStore(subscribeMQL, getMQLSnapshot, getMQLServerSnapshot);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Extract `data-*` attributes (excluding `data-testid`, which has its own
 * dedicated prop) from the public Sheet props so they can be spread onto the
 * underlying Content element.
 *
 * Preserves the `data-capsule-passthrough="true"` opt-out path used by the
 * search palette to avoid auto-minimising the floating capsule.
 */
function extractDataAttrs(props: SheetCommonProps): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    if (!k.startsWith("data-")) continue;
    if (k === "data-testid") continue;
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

// The legacy detent path renders a static-height sheet sized by CSS
// (40 / 60 / 92 dvh per `[data-detent]` rules in Sheet.css). It does NOT
// pass `snapPoints` to Vaul — see the comment in SheetLegacy's mobile
// branch for the iOS 18 Safari bug that forced removal of the
// single-element snap-points array. Drag-to-dismiss still works via
// Vaul's default downward-drag handler.

const NAMED_SNAP: Record<"peek" | "medium" | "full", number> = {
  peek: 0.4,
  medium: 0.6,
  full: 0.92,
};

/**
 * Resolve a snap-point token to a numeric fraction. Numbers pass through.
 * Throws (via console.error → fallback 0.92) on out-of-range numerics so a
 * caller mistake is surfaced loudly rather than silently mis-rendering.
 */
function resolveSnap(point: SheetSnapPoint): number {
  if (typeof point === "number") {
    if (point > 0 && point <= 1) return point;
    // Rule 8 — log loudly; degrade to full-height fallback.
    // eslint-disable-next-line no-console
    console.error(
      `[Sheet] snap-point ${String(point)} is out of (0, 1] range; falling back to 0.92.`,
    );
    return 0.92;
  }
  return NAMED_SNAP[point];
}

/**
 * Resolve a snap-point list. Empty / undefined input returns `[0.92]` (single
 * full-height snap-point, equivalent to a modal).
 */
function resolveSnapList(points: SheetSnapPoint[] | undefined): number[] {
  if (!points || points.length === 0) return [0.92];
  return points.map(resolveSnap);
}

/**
 * Map a numeric snap-point fraction to the closest legacy detent token, for
 * desktop CSS sizing. Used when the controlled API renders on desktop — the
 * `proof-sheet-panel[data-detent]` CSS keys off this attribute, and Wave 5's
 * sheets need a sensible default desktop size.
 */
function snapToDetent(snap: number): SheetDetent {
  if (snap <= 0.45) return "peek";
  if (snap <= 0.75) return "medium";
  return "large";
}

// ── Sheet ─────────────────────────────────────────────────────────────────────

/**
 * Sheet — bottom-sheet portal.
 *
 * Accepts both the legacy `<Sheet detent onDismiss>` API and the new
 * controlled `<Sheet open onOpenChange snapPoints dismissGuard>` API. The two
 * shapes are distinguished by the presence of `onOpenChange`.
 *
 * On mobile widths, composes Vaul's `Drawer.*` primitives: drag-to-dismiss,
 * snap-points, iOS keyboard handling, and visual-viewport anchor are all
 * solved by the library. On desktop widths, composes Radix `Dialog.*`:
 * focus-trap, focus-restore, scroll-lock, ESC and outside-click are all
 * library-handled.
 *
 * The Content element carries `proof-sheet-panel` plus `data-detent` so the
 * existing CSS detent rules in Sheet.css continue to apply.
 */
export function Sheet(props: SheetProps | SheetControlledProps) {
  if (isControlled(props)) {
    return <SheetControlled {...props} />;
  }
  return <SheetLegacy {...props} />;
}

// ── Sheet — legacy API ────────────────────────────────────────────────────────

/**
 * Legacy "always-mounted-when-open" implementation. The parent renders this
 * conditionally; Vaul/Radix always sees `open={true}`. `onDismiss` is the
 * single uniform close callback.
 *
 * Drag-to-dismiss vs onDismiss contract:
 * - The mobile drawer auto-closes on a downward drag past Vaul's threshold.
 * - The mobile and desktop variants both close on ESC, scrim tap, and
 *   programmatic close calls.
 * - All three close paths route through `onOpenChange(false)` → `onDismiss()`,
 *   so consumers get a single uniform callback.
 */
function SheetLegacy(props: SheetProps) {
  const { detent = "medium", onDismiss, children, "aria-label": ariaLabel } = props;
  const testId = props["data-testid"] ?? "sheet-panel";
  const isDesktop = useIsDesktop();
  const dataAttrs = extractDataAttrs(props);

  /*
   * Capture the element that was focused at mount time so we can restore
   * focus to it on unmount. The Sheet API does not require consumers to
   * use a Radix-style `Dialog.Trigger` — they pass their own trigger
   * buttons — so Radix's built-in trigger-ref-based focus restoration
   * cannot find the right element. We capture document.activeElement on
   * mount and re-focus it from `onCloseAutoFocus`.
   */
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    typeof document !== "undefined" && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  // Both libraries treat `open` as controlled; we keep Sheet itself un-mounted
  // by the consumer when closed, so `open={true}` is the only path that
  // reaches this render. `onOpenChange(false)` fires for ESC / scrim-tap /
  // drag-to-dismiss / programmatic close — we forward each to onDismiss().
  function handleOpenChange(next: boolean) {
    if (!next) onDismiss();
  }

  /**
   * Override Radix's default `onCloseAutoFocus` (which calls
   * `triggerRef.current?.focus()` and is a no-op when no Dialog.Trigger
   * exists). Restore focus to the element that had focus when the Sheet
   * mounted.
   */
  function handleCloseAutoFocus(event: Event) {
    event.preventDefault();
    const prev = previouslyFocusedRef.current;
    if (prev && typeof prev.focus === "function") {
      prev.focus();
    }
  }

  if (isDesktop) {
    return (
      <Dialog.Root open={true} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <div className="proof-sheet-root">
            <Dialog.Overlay
              className="proof-sheet-scrim"
              data-testid="sheet-scrim"
              onClick={() => onDismiss()}
            />
            <Dialog.Content
              className="proof-sheet-panel"
              data-detent={detent}
              data-testid={testId}
              aria-label={ariaLabel}
              aria-modal="true"
              aria-describedby={undefined}
              aria-labelledby={undefined}
              onCloseAutoFocus={handleCloseAutoFocus}
              {...dataAttrs}
            >
              <Dialog.Title aria-hidden="true" style={{ display: "none" }} />
              {children}
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // ── Mobile: Vaul drawer ────────────────────────────────────────────────────
  //
  // iOS 18 Safari Sheet rendering fix (bugs #185, #188, #193):
  //
  // The legacy detent API renders a static-height sheet (peek / medium / large
  // = 40 / 60 / 92 dvh). Earlier this branch passed `snapPoints` to Vaul as a
  // single-element array so Vaul would resolve the panel position. That was
  // wrong: with `snapPoints` set, Vaul writes `transform: translate3d(0,
  // --snap-point-height, 0)` to the panel where `--snap-point-height =
  // (1 - snapPoint) * window.innerHeight`. The transform is *relative to the
  // element's natural document-flow position*. Without an explicit
  // `position: fixed` on `.proof-sheet-panel`, the panel lays out at the end
  // of `document.body` (which is itself `position: fixed; top: -scrollPos`
  // under Vaul's scroll-lock), and the translateY pushes it entirely below
  // the visual viewport on iOS 18 Safari — the scrim renders but the panel
  // is offscreen.
  //
  // The right model for a static-height detent: drop `snapPoints` entirely.
  // Vaul falls back to its `slideFromBottom` keyframe (translate from
  // `var(--initial-transform, 100%)` to `0`) which only works when the panel
  // is `position: fixed; bottom: 0`. The matching CSS lives in Sheet.css
  // (`.proof-sheet-panel` mobile rules). Drag-to-dismiss still works via
  // Vaul's default downward-drag-past-threshold behaviour.
  //
  // `repositionInputs={false}` because no legacy consumer has a text input;
  // Vaul's visualViewport-reposition hook adds non-trivial style mutation on
  // mount that we don't need here.
  return (
    <Drawer.Root
      open={true}
      onOpenChange={handleOpenChange}
      shouldScaleBackground={false}
      repositionInputs={false}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          className="proof-sheet-scrim"
          data-testid="sheet-scrim"
          onClick={() => onDismiss()}
        />
        <Drawer.Content
          className="proof-sheet-panel"
          data-detent={detent}
          data-testid={testId}
          aria-label={ariaLabel}
          aria-modal="true"
          aria-describedby={undefined}
          aria-labelledby={undefined}
          onCloseAutoFocus={handleCloseAutoFocus}
          {...dataAttrs}
        >
          <Drawer.Title aria-hidden="true" style={{ display: "none" }} />
          <Drawer.Handle className="proof-sheet-handle" aria-hidden="true" />
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

// ── Sheet — new controlled API ────────────────────────────────────────────────

/**
 * Controlled-API implementation. Renders nothing when `open === false`;
 * mounts on open. Supports `snapPoints`, `defaultSnapPoint`, and the
 * `dismissGuard` unsaved-changes contract.
 */
function SheetControlled(props: SheetControlledProps) {
  const {
    open,
    onOpenChange,
    snapPoints,
    defaultSnapPoint,
    dismissGuard,
    onDismissGuardError,
    children,
    "aria-label": ariaLabel,
    side = "bottom",
  } = props;
  const testId = props["data-testid"] ?? "sheet-panel";
  const isDesktop = useIsDesktop();
  const dataAttrs = extractDataAttrs(props);

  // Snap-point resolution. Memoised via state initializer pattern to avoid
  // recomputing on every render — Vaul holds the array reference and a new
  // reference each render would re-anchor the drawer.
  const resolvedSnaps = resolveSnapList(snapPoints);
  const initialSnap = defaultSnapPoint ? resolveSnap(defaultSnapPoint) : resolvedSnaps[0];

  // Active snap-point — Vaul drives this on mobile via drag; on desktop we
  // pin to the largest snap-point (desktop is a static centred modal).
  const [activeSnap, setActiveSnap] = useState<number | string | null>(initialSnap ?? 0.92);

  // Reset active snap when the controlled `open` toggles to true so a
  // re-opened sheet always starts at `defaultSnapPoint`.
  useEffect(() => {
    if (open) setActiveSnap(initialSnap ?? 0.92);
  }, [open, initialSnap]);

  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      previouslyFocusedRef.current = document.activeElement;
    }
  }, [open]);

  /**
   * Run the dismiss guard. Returns a promise that resolves to `true` (proceed
   * with close) or `false` (cancel). Synchronous booleans are wrapped in
   * `Promise.resolve`. Rejections are logged (Rule 8 — never silently
   * swallow); `onDismissGuardError` is called if provided.
   */
  async function runDismissGuard(): Promise<boolean> {
    if (!dismissGuard) return true;
    try {
      const result = dismissGuard();
      // Allow promise OR sync boolean.
      const settled = await Promise.resolve(result);
      return settled !== false;
    } catch (err) {
      // Rule 8 — never silently swallow. Log + propagate to optional callback.
      // eslint-disable-next-line no-console
      console.error("[Sheet] dismissGuard rejected; cancelling close.", err);
      if (onDismissGuardError) {
        try {
          onDismissGuardError(err);
        } catch (cbErr) {
          // eslint-disable-next-line no-console
          console.error("[Sheet] onDismissGuardError callback threw.", cbErr);
        }
      }
      return false;
    }
  }

  /**
   * Forward close-intent through the dismiss-guard. If the guard returns
   * (or resolves) `false` / rejected, we cancel by NOT calling onOpenChange.
   *
   * For Vaul/Radix, we intercept their `onOpenChange(false)` callback. The
   * library itself does not know about our guard; we re-open the sheet (by
   * keeping `open={true}` in our state) if the guard cancels. Because the
   * underlying library has already started its close animation, the visible
   * effect is a brief flicker. The cost is acceptable: the alternative is to
   * not delegate to the library at all (we lose drag-to-dismiss).
   *
   * If the guard is synchronous and returns false, we never call
   * onOpenChange(false) so the parent's state stays `open=true` and the
   * library re-receives `open=true` on the next render, re-opening the sheet.
   */
  function handleOpenChange(next: boolean) {
    if (next) {
      // Open: bypass guard. Only the parent fires `open=true`; this branch is
      // really only hit if Vaul/Radix toggle `open=true` themselves, which
      // they don't — but we forward defensively.
      onOpenChange(true);
      return;
    }
    // Close: run guard.
    void (async () => {
      const allow = await runDismissGuard();
      if (allow) {
        onOpenChange(false);
      } else {
        // Guard cancelled. We must trigger a re-render so the underlying
        // library sees `open={true}` again. Updating local state is the
        // cheapest way — but we already have `open` as a controlled prop, so
        // we don't need to do anything; React will re-render with the
        // parent's `open={true}` still in place and Vaul/Radix will reopen.
        // To be safe, nudge the active-snap state so we get a re-render.
        setActiveSnap((v) => v);
      }
    })();
  }

  function handleCloseAutoFocus(event: Event) {
    event.preventDefault();
    const prev = previouslyFocusedRef.current;
    if (prev && typeof prev.focus === "function") {
      prev.focus();
    }
  }

  if (!open) return null;

  // Largest snap-point — used for desktop CSS sizing.
  const largestSnap = resolvedSnaps[resolvedSnaps.length - 1] ?? 0.92;
  const desktopDetent = snapToDetent(largestSnap);

  if (isDesktop) {
    return (
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <div className="proof-sheet-root">
            <Dialog.Overlay
              className="proof-sheet-scrim"
              data-testid="sheet-scrim"
              onClick={() => handleOpenChange(false)}
            />
            <Dialog.Content
              className="proof-sheet-panel"
              data-detent={desktopDetent}
              data-side={side}
              data-testid={testId}
              aria-label={ariaLabel}
              aria-modal="true"
              aria-describedby={undefined}
              aria-labelledby={undefined}
              onCloseAutoFocus={handleCloseAutoFocus}
              /*
               * Intercept Radix's pointer-down-outside / escape-key handlers
               * so the guard runs even when the dismissal originates from
               * Radix internals rather than our explicit onClick on the
               * scrim. Calling event.preventDefault() inside these handlers
               * tells Radix to keep the dialog open — we still need to call
               * the guard manually because Radix won't ever fire
               * `onOpenChange(false)` on a prevented close.
               */
              onPointerDownOutside={(event) => {
                if (!dismissGuard) return; // fall through to default Radix behaviour
                event.preventDefault();
                handleOpenChange(false);
              }}
              onEscapeKeyDown={(event) => {
                if (!dismissGuard) return;
                event.preventDefault();
                handleOpenChange(false);
              }}
              {...dataAttrs}
            >
              <Dialog.Title aria-hidden="true" style={{ display: "none" }} />
              {children}
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // ── Mobile: Vaul drawer with multi-snap ────────────────────────────────────
  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      snapPoints={resolvedSnaps}
      activeSnapPoint={activeSnap}
      setActiveSnapPoint={setActiveSnap}
      shouldScaleBackground={false}
      repositionInputs={true}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          className="proof-sheet-scrim"
          data-testid="sheet-scrim"
          onClick={() => handleOpenChange(false)}
        />
        <Drawer.Content
          className="proof-sheet-panel"
          data-detent={desktopDetent}
          data-side={side}
          data-testid={testId}
          aria-label={ariaLabel}
          aria-modal="true"
          aria-describedby={undefined}
          aria-labelledby={undefined}
          onCloseAutoFocus={handleCloseAutoFocus}
          {...dataAttrs}
        >
          <Drawer.Title aria-hidden="true" style={{ display: "none" }} />
          <Drawer.Handle className="proof-sheet-handle" aria-hidden="true" />
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
